import { useCallback, useMemo } from 'react';
import { type RequestPolicy } from '@urql/core';

import { useIsLoggedIn } from '@order/AuthMachine';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useGetUserNotificationPreferencesQuery } from './graphql/GetUserNotificationPreferences.generated';
import { NotificationCategoryType } from './useNotificationPreferencesState.types';

// ─────────────────────────────────────────────────────────────────────

export const useNotificationPreferencesState = (params?: Params) => {
  const { requestPolicy, pause } = params ?? {};

  // ─── Flags ───────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();
  const isNotificationPreferencesEnabled = useFeatureFlag(
    'CELS-3674-enable-notification-preference-center',
  );

  // ─────────────────────────────────────────────────────────────────────

  const [{ fetching, stale, error, data }, refetch] =
    useGetUserNotificationPreferencesQuery({
      requestPolicy,
      pause: pause || !isNotificationPreferencesEnabled || !isLoggedIn,
    });

  // ─────────────────────────────────────────────────────────────────────

  const resolvedData = data?.getUserNotificationPreferences;
  const hasValidPreferences = resolvedData?.__typename === 'UserNotificationPreferences'; // prettier-ignore

  const isOrderPushEnabled = useMemo(() => {
    if (!hasValidPreferences) return false;

    const orderStatusPreference = resolvedData.notificationCategories.find(
      (category) => category.id === NotificationCategoryType.OrderStatus,
    );

    return orderStatusPreference?.pushSubscribe ?? false;
  }, [hasValidPreferences, resolvedData]);

  // ─── Helpers ─────────────────────────────────────────────────────────

  // NOTE: An additional wrapper is used to ensure that no extra arguments
  //       are passed to the refetch function.
  const refetchPreferences = useCallback(() => {
    refetch({ requestPolicy: 'cache-and-network' });
  }, [refetch]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    loading: fetching || stale,
    error,
    refetch: refetchPreferences,
    isOrderPushEnabled,
    preferences: hasValidPreferences
      ? resolvedData.notificationCategories
      : null,
  };
};

// ─── Types ───────────────────────────────────────────────────────────

type Params = {
  requestPolicy?: RequestPolicy;
  pause?: boolean;
};
